import React, { useEffect, useState } from 'react'
import cx from 'classnames'
import { useDevice } from 'device'
import { type ModalComponentProps } from 'modals'
import { track } from 'analytics'

import { getRecommendedProductCnstrcAttrs } from 'modules/constructorIO'

import { ModalButtons, ModalHeadline, PlainModal } from 'components/feedback'
import { buttonMessages } from 'components/inputs'

import ProductCardSmallSkeleton from 'compositions/productCards/ProductCardSmallSkeleton/ProductCardSmallSkeleton'
import ProductCardSmall from 'compositions/productCards/ProductCardSmall/ProductCardSmall'

import { useFirstProducts } from './utils'

import messages from './messages'


type FirstProductModalProps = {
  selectedTradingItemUid: string
  onContinue: (product: ProductFragment.Base) => void
}

export const FirstProductModalView: React.FC<FirstProductModalProps & Pick<ModalComponentProps, 'closeModal'>> = (props) => {
  const { selectedTradingItemUid, onContinue, closeModal } = props
  const { isMobile } = useDevice()

  const [ selectedItem, setSelectedItem ] = useState<ProductFragment.Base>(null)

  const { dataAttributes, isFetching, products, recommenderName, recommenderStrategies } = useFirstProducts()

  const handleContinue = () => {
    const { volume } = selectedItem.tradingItems?.items?.[0]

    track('First product change', {
      productId: selectedItem.id,
      productGender: selectedItem.gender,
      productFullName: selectedItem.fullName,
      productBrand: selectedItem.brandInfo.name,
      productCategory: selectedItem.category,
      productVolume: volume ? `${volume.volume} ${volume.unit}` : undefined,
    })

    onContinue(selectedItem)
    closeModal()
  }

  // if selected product is among products, we should pre-select it
  useEffect(() => {
    if (products) {
      const selectedProduct = products.find(({ tradingItems }) => tradingItems?.items?.[0].uid === selectedTradingItemUid)

      if (selectedProduct) {
        setSelectedItem(selectedProduct)
      }

      track('Recommendations display', {
        placement: 'First product popup',
        recommenderName,
      })
    }
  }, [ products, recommenderName, selectedTradingItemUid ])

  const buttonsClassName = cx(
    'z-floating-header sticky bottom-0',
    isMobile
      ? 'bg-gold-30 -mx-16 -mb-16 mt-32 pb-32'
      : 'bg-gray-10 -mx-40 -mb-40 p-40'
  )

  return (
    <>
      <ModalHeadline
        title={messages.title}
        text={messages.text}
      />
      <div className="grid grid-cols-2 gap-16" {...dataAttributes}>
        {
          isFetching ? (
            new Array(6).fill(null).map((value, index) => (
              <ProductCardSmallSkeleton key={index} />
            ))
          ) : (
            products?.map((item) => {
              const { uid } = item

              const attributes = getRecommendedProductCnstrcAttrs({
                recommenderStrategies,
                uid,
              })

              return (
                <ProductCardSmall
                  key={uid}
                  data={item}
                  withoutLink
                  buttonProps={{
                    type: 'custom',
                    title: messages.try,
                    addedMessage: messages.selected,
                    isAdded: selectedItem === item,
                    onClick: () => {
                      setSelectedItem(item)
                    },
                  }}
                  dataAttributes={attributes}
                />
              )
            })
          )
        }
      </div>
      <ModalButtons
        className={buttonsClassName}
        primary={{
          title: buttonMessages.continue,
          fullWidth: true,
          disabled: !selectedItem,
          onClick: handleContinue,
        }}
      />
    </>
  )
}

const FirstProductModal: React.FC<FirstProductModalProps & ModalComponentProps> = (props) => {
  const { name, closeModal, selectedTradingItemUid, onContinue } = props
  const { isMobile } = useDevice()

  return (
    <PlainModal
      type={isMobile ? 'default' : 'sidebar-right'}
      closeModal={closeModal}
      data-testid={name}
    >
      <FirstProductModalView
        selectedTradingItemUid={selectedTradingItemUid}
        onContinue={onContinue}
        closeModal={closeModal}
      />
    </PlainModal>
  )
}


export default FirstProductModal
